import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";

const STATUS_COLORS = {
  CONFIRMED:   "bg-emerald-100 text-emerald-700",
  CANCELLED:   "bg-red-100 text-red-700",
  COMPLETED:   "bg-slate-100 text-slate-700",
  NO_SHOW:     "bg-amber-100 text-amber-700",
  RESCHEDULED: "bg-blue-100 text-blue-700",
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

export default function SchedulingBookings() {
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Bookings - Ample Hire";
    setLoading(true);
    Promise.all([
      axiosInstance.get(`${baseUrl}/job-posting/scheduling/bookings`),
      axiosInstance.get(`${baseUrl}/job-posting/scheduling/links`),
    ]).then(([rb, rl]) => {
      setBookings(Array.isArray(rb.data) ? rb.data : []);
      setLinks(Array.isArray(rl.data) ? rl.data : []);
    }).catch(() => { setBookings([]); setLinks([]); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Interview bookings</h1>
          <p className="text-sm text-vf-muted mt-1">All scheduled interviews and pending scheduling links.</p>
        </div>
        <button onClick={() => nav("/admin/scheduling-create")} className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs">
          + Send scheduling link
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <h3 className="text-sm font-semibold text-vf-base mb-2">Booked interviews</h3>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-vf-muted">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Candidate</th>
                  <th className="text-left px-3 py-2 font-medium">When</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && <tr><td colSpan={3} className="px-3 py-6 text-center text-vf-muted text-xs">Loading…</td></tr>}
                {!loading && bookings.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-vf-muted text-xs">No bookings yet.</td></tr>
                )}
                {bookings.map((b) => (
                  <tr key={b.id}>
                    <td className="px-3 py-2 text-vf-base">#{b.candidateId}</td>
                    <td className="px-3 py-2 text-vf-muted text-xs">{fmt(b.startTime)}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[b.status] || "bg-slate-100"}`}>
                        {b.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-vf-base mb-2">Pending scheduling links</h3>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-vf-muted">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Candidate</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Link</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!loading && links.filter((l) => l.status === "PENDING").length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-vf-muted text-xs">No pending links.</td></tr>
                )}
                {links.filter((l) => l.status === "PENDING").map((l) => (
                  <tr key={l.id}>
                    <td className="px-3 py-2 text-vf-base">#{l.candidateId}</td>
                    <td className="px-3 py-2 text-vf-muted text-xs">{l.status}</td>
                    <td className="px-3 py-2 text-vf-muted text-xs">
                      <a href={`/schedule/${l.token}`} target="_blank" rel="noreferrer" className="text-vf-primary underline">
                        Open
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
